import { SWRConfig } from 'swr'
import { Provider, useAtomValue } from 'jotai'
import { useEffect } from 'react'
import { loginStateAtom, store, syncLoginStateAtom } from '@/store'
import { ConfigProvider } from 'antd-mobile'
import enUS from 'antd-mobile/es/locales/en-US'
import { Outlet } from 'react-router-dom'
import { GlobalLoading } from '@/components/GlobalLoading'
import Layout from '@/components/layout'
import { ThemeProvider, UserGuideProvider, RxWebsocketProvider } from 'react-provider'
import { AliveScope } from 'react-activation'
import Icon from '@/components/Icon'
import { addUserGuide, getUserGuide } from '@/api'
import { getCookieAuthInfo, getGlobalConfig, setCookieAuthInfo } from '@/utils'
import { useGlobalErrorHandler, useWebsocketHandlers } from '@/hooks'
import BanModal from '@/components/BanModal'

const GUIDE_PROVIDER_ROUTER = [
  { pattern: '/home', type: 1 },
  { pattern: '/home/discover', type: 2 },
  { pattern: '/message', type: 3 },
  { pattern: '/personal', type: 4 },
  { pattern: '/cover/:id', type: 5 },
  { pattern: '/detail/novel/:resourceInfo', type: 6 },
  // '/account'
]

const LoginStateSubscriber = ({ children }: { children: React.ReactNode }) => {
  const isLogin = useAtomValue(loginStateAtom)

  return (
    <RxWebsocketProvider
      rxWebSocketConfig={{
        url: getGlobalConfig().VITE_WS_ENDPOINT,
        autoConnect: true,
        queryParams: () => {
          const { token } = getCookieAuthInfo('token')
          return { token: token || '' }
        },
      }}
      useWebsocketHandlers={useWebsocketHandlers}
      isLoggedIn={isLogin}
    >
      <GlobalLoading />
      <BanModal />
      <UserGuideProvider
        routes={GUIDE_PROVIDER_ROUTER}
        currentPath={location.pathname}
        isLoggedIn={isLogin}
        api={{
          getUserGuide: getUserGuide,
          setUserGuide: addUserGuide,
        }}
        popoverClassName='custom-driver-popover'
      >
        <Layout>{children}</Layout>
      </UserGuideProvider>
    </RxWebsocketProvider>
  )
}

// 监听 pageshow 并在 bfcache 恢复时把 cookie 中的登录态强制写回 atom。
// 该组件放在 AliveScope 之外的 Provider 内部，确保 Safari 恢复页面时 effect 能可靠触发，
// 不会被 react-activation 的 keep-alive 行为吞掉。
const BfcacheLoginResyncer = () => {
  useEffect(() => {
    const handlePageShow = (event: PageTransitionEvent) => {
      if (event.persisted) {
        store.set(syncLoginStateAtom)
      }
    }

    window.addEventListener('pageshow', handlePageShow)

    return () => {
      window.removeEventListener('pageshow', handlePageShow)
    }
  }, [])

  return null
}

const RouterLayout = () => {
  useGlobalErrorHandler()

  return (
    <ConfigProvider locale={enUS} input={{ clearIcon: <Icon name='Cancel' /> }}>
      <SWRConfig
        value={{
          revalidateOnFocus: false,
        }}
      >
        <Provider store={store}>
          <ThemeProvider
            options={{
              storageAdapter: {
                getTheme: () => {
                  const { darkMode } = getCookieAuthInfo('darkMode')
                  return JSON.parse(darkMode || 'null')
                },
                setTheme: (isDark) => {
                  setCookieAuthInfo('darkMode', JSON.stringify(isDark))
                },
              },
            }}
          >
            <BfcacheLoginResyncer />
            <LoginStateSubscriber>
              <AliveScope>
                <Outlet />
              </AliveScope>
            </LoginStateSubscriber>
          </ThemeProvider>
        </Provider>
      </SWRConfig>
    </ConfigProvider>
  )
}

export default RouterLayout
